<template>
  <b-modal
    v-model="isVisible"
    size="xl"
    title="Подробный прогресс студента"
    @hidden="resetData"
    hide-footer
    scrollable
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Загрузка...</span>
      </div>
      <p class="mt-3 text-muted">Загрузка подробных данных...</p>
    </div>

    <!-- Student Details -->
    <div v-else-if="studentData" class="student-details">
      <!-- Student Info Header -->
      <div class="student-header mb-4">
        <div class="row">
          <div class="col-md-8">
            <div class="d-flex align-items-center">
              <div class="avatar-wrapper mr-3">
                <img
                  v-if="studentData.student_info.avatar"
                  :src="studentData.student_info.avatar"
                  :alt="studentData.student_info.name"
                  class="avatar"
                />
                <div v-else class="avatar-placeholder">
                  <feather-icon icon="UserIcon" size="32" />
                </div>
              </div>
              <div>
                <h3 class="mb-1">{{ studentData.student_info.name }}</h3>
                <p class="text-muted mb-0">{{ studentData.student_info.email }}</p>
              </div>
            </div>
          </div>
          <div class="col-md-4 text-right">
            <b-button
              variant="outline-primary"
              @click="downloadReport"
              :disabled="isDownloading"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
              {{ isDownloading ? 'Загрузка...' : 'Скачать отчет' }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <b-tabs v-model="activeTab" nav-wrapper-class="mb-3">
        <!-- Attendance Tab -->
        <b-tab title="Посещаемость" active>
          <AttendanceDetails :attendance-data="studentData.lesson_attendance" />
        </b-tab>

        <!-- Homework Tab -->
        <b-tab title="Домашние задания">
          <HomeworkDetails :homework-data="studentData.homework_performance" />
        </b-tab>

        <!-- Tests Tab -->
        <b-tab title="Тесты">
          <TestDetails :test-data="studentData.test_performance" />
        </b-tab>

        <!-- Lesson Packages Tab -->
        <b-tab title="Пакеты уроков">
          <LessonPackagesDetails :packages-data="studentData.lesson_packages" />
        </b-tab>

        <!-- Analytics Tab -->
        <b-tab title="Аналитика">
          <ProgressAnalytics :analytics-data="studentData.progress_analytics" />
        </b-tab>
      </b-tabs>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <div class="d-flex align-items-center">
        <feather-icon icon="AlertCircleIcon" size="20" class="mr-2" />
        <div>
          <strong>Ошибка загрузки данных:</strong> {{ error }}
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BTabs,
  BTab,
  BButton,
  BAlert
} from 'bootstrap-vue'
import AttendanceDetails from './details/AttendanceDetails.vue'
import HomeworkDetails from './details/HomeworkDetails.vue'
import TestDetails from './details/TestDetails.vue'
import LessonPackagesDetails from './details/LessonPackagesDetails.vue'
import ProgressAnalytics from './details/ProgressAnalytics.vue'
import ParentalControlAPI from '@/api/parentalControl'

export default {
  name: 'StudentDetailsModal',
  components: {
    BModal,
    BTabs,
    BTab,
    BButton,
    BAlert,
    AttendanceDetails,
    HomeworkDetails,
    TestDetails,
    LessonPackagesDetails,
    ProgressAnalytics
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    studentId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      isVisible: this.value,
      isLoading: false,
      isDownloading: false,
      error: null,
      studentData: null,
      activeTab: 0
    }
  },
  watch: {
    value(newVal) {
      this.isVisible = newVal
      if (newVal && this.studentId) {
        this.loadStudentDetails()
      }
    },
    isVisible(newVal) {
      this.$emit('input', newVal)
    },
    studentId(newVal) {
      if (newVal && this.isVisible) {
        this.loadStudentDetails()
      }
    }
  },
  methods: {
    async loadStudentDetails() {
      if (!this.studentId) return

      this.isLoading = true
      this.error = null

      try {
        const response = await ParentalControlAPI.getStudentProgress(this.studentId)
        this.studentData = response.data
      } catch (error) {
        this.error = 'Не удалось загрузить подробные данные студента'
        console.error('Student details loading error:', error)
      } finally {
        this.isLoading = false
      }
    },
    async downloadReport() {
      if (!this.studentData) return

      this.isDownloading = true

      try {
        // Create a simple text report (could be enhanced with PDF generation)
        const reportData = this.generateReportData()
        const blob = new Blob([reportData], { type: 'text/plain;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `progress_report_${this.studentData.student_info.name}_${new Date().toISOString().split('T')[0]}.txt`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)

        this.$bvToast.toast('Отчет успешно скачан', {
          title: 'Успех',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast('Ошибка при скачивании отчета', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isDownloading = false
      }
    },
    generateReportData() {
      const student = this.studentData.student_info
      const attendance = this.studentData.lesson_attendance
      const homework = this.studentData.homework_performance
      const tests = this.studentData.test_performance
      const packages = this.studentData.lesson_packages

      return `
ОТЧЕТ О ПРОГРЕССЕ СТУДЕНТА
==========================

Студент: ${student.name}
Email: ${student.email}
Дата отчета: ${new Date().toLocaleDateString('ru-RU')}

ПОСЕЩАЕМОСТЬ
============
Всего уроков: ${attendance.total_lessons || 0}
Посещено: ${attendance.attended || 0}
Пропущено: ${attendance.missed || 0}
Процент посещаемости: ${attendance.attendance_rate || 0}%

ДОМАШНИЕ ЗАДАНИЯ
===============
Всего заданий: ${homework.length || 0}
Выполнено: ${homework.filter(h => h.status === 'graded').length || 0}
Средний балл: ${homework.reduce((sum, h) => sum + (h.score || 0), 0) / (homework.length || 1)}

ТЕСТЫ
=====
Всего тестов: ${tests.length || 0}
Средний балл: ${tests.reduce((sum, t) => sum + (t.score || 0), 0) / (tests.length || 1)}%

ПАКЕТЫ УРОКОВ
============
Активных пакетов: ${packages.length || 0}
Общее количество уроков: ${packages.reduce((sum, p) => sum + (p.lessons_total || 0), 0)}
Использовано уроков: ${packages.reduce((sum, p) => sum + (p.lessons_used || 0), 0)}
Осталось уроков: ${packages.reduce((sum, p) => sum + (p.lessons_remaining || 0), 0)}

===========================
Отчет сгенерирован автоматически
      `.trim()
    },
    resetData() {
      this.studentData = null
      this.error = null
      this.activeTab = 0
    }
  }
}
</script>

<style lang="scss" scoped>
.student-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;

  .avatar-wrapper {
    .avatar {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      object-fit: cover;
    }

    .avatar-placeholder {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: #f8f9fa;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6c757d;
    }
  }
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.dark-layout {
  .student-header {
    border-bottom-color: #404656;

    .avatar-placeholder {
      background: #404656;
      color: #b4b7bd;
    }
  }

  .modal-body {
    background-color: #1e1e2e;
    color: #d0d2d6;
  }
}

@media (max-width: 768px) {
  .student-header {
    .col-md-4 {
      margin-top: 1rem;
      text-align: left !important;
    }
  }
}
</style>